import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiEdit2, FiArrowLeft, FiCheck, FiPackage, FiFileText } from 'react-icons/fi';

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/customers/${id}`).then(r => { setCustomer(r.data.customer); setLoading(false); })
      .catch(() => { toast.error('Customer not found'); navigate('/customers'); });
  }, [id, navigate]);

  const markPaid = async (mId) => {
    try {
      await api.put(`/customers/${id}/measurement/${mId}/paid`);
      toast.success('Marked as paid');
      const r = await api.get(`/customers/${id}`);
      setCustomer(r.data.customer);
    } catch { toast.error('Failed'); }
  };

  const markDelivered = async (mId) => {
    try {
      await api.put(`/customers/${id}/measurement/${mId}/deliver`);
      toast.success('Marked as delivered');
      const r = await api.get(`/customers/${id}`);
      setCustomer(r.data.customer);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!customer) return null;

  const m = customer.measurements?.[0];

  return (
    <div>
      <div style={{ display:'flex', gap:10, marginBottom:20, alignItems:'center' }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/customers')}>
          <FiArrowLeft size={14} /> Back
        </button>
        {!m?.isDelivered && (
          <Link to={`/customers/${id}/edit`} className="btn btn-outline btn-sm">
            <FiEdit2 size={14} /> Edit
          </Link>
        )}
        <Link to="/bills/create" className="btn btn-accent btn-sm">
          <FiFileText size={14} /> Create Bill
        </Link>
      </div>

      {/* Customer Info */}
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header">
          <h3 className="card-title">👤 {customer.name}</h3>
          {m?.isPending
            ? <span className="badge badge-warning">Payment Pending</span>
            : m?.isDelivered
              ? <span className="badge badge-success">Delivered</span>
              : <span className="badge badge-gray">Active</span>}
        </div>
        <div className="card-body">
          <div className="form-row-3">
            <div><span style={{ color:'#94a3b8', fontSize:11, fontWeight:600, textTransform:'uppercase' }}>Phone</span>
              <div style={{ fontWeight:500, marginTop:4 }}>{customer.phone}</div></div>
            <div><span style={{ color:'#94a3b8', fontSize:11, fontWeight:600, textTransform:'uppercase' }}>City</span>
              <div style={{ fontWeight:500, marginTop:4 }}>{customer.city || '-'}</div></div>
            <div><span style={{ color:'#94a3b8', fontSize:11, fontWeight:600, textTransform:'uppercase' }}>Joined</span>
              <div style={{ fontWeight:500, marginTop:4 }}>{new Date(customer.createdAt).toLocaleDateString()}</div></div>
          </div>
        </div>
      </div>

      {/* Measurements */}
      {m && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header">
            <h3 className="card-title">📐 Measurements</h3>
            <div style={{ display:'flex', gap:8 }}>
              {m.isPending && (
                <button className="btn btn-success btn-sm" onClick={() => markPaid(m._id)}>
                  <FiCheck size={13} /> Mark Paid
                </button>
              )}
              {!m.isDelivered && (m.remaining || 0) <= 0.01 && (
                <button className="btn btn-primary btn-sm" onClick={() => markDelivered(m._id)}>
                  <FiPackage size={13} /> Mark Delivered
                </button>
              )}
              {/* Cloth can't be handed over until the balance is fully settled — collect the
                  remaining payment (or apply a discount) before Mark Delivered appears. */}
              {!m.isDelivered && (m.remaining || 0) > 0.01 && (
                <span className="badge badge-warning" title="Collect the full remaining balance to enable delivery">
                  SAR {m.remaining} remaining before delivery
                </span>
              )}
            </div>
          </div>
          <div className="card-body">
            {/* Order Info */}
            <div className="form-row-3" style={{ marginBottom:16 }}>
              <div><span style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>TYPE</span>
                <div style={{ fontWeight:600, marginTop:4 }}>{m.type || '-'}</div></div>
              <div><span style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>DELIVERY DATE</span>
                <div style={{ fontWeight:600, marginTop:4 }}>
                  {m.deliveryDate ? new Date(m.deliveryDate).toLocaleDateString('en-AE',{day:'numeric',month:'short',year:'numeric'}) : '-'}
                </div></div>
              <div><span style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>QUANTITY</span>
                <div style={{ fontWeight:600, marginTop:4 }}>{m.suitQuantity || 1}</div></div>
            </div>

            {/* Measurements Grid */}
            <div style={{ background:'#f8fafc', borderRadius:8, padding:16, marginBottom:16 }}>
              <div className="measurement-grid">
                {[['Length',m.length],['Shoulder',m.shoulder],['Arm',m.arm],
                  ['Arm Hole',m.armHole],['Neck',m.neck],['Chest',m.chest],
                  ['Waist',m.waist],['Surround',m.surround],['Pant Length',m.pantLength],
                  ['Pant Waist',m.pantWaist],['Ankle',m.ankle],['Wrist',m.wrist],['Plate',m.plate]
                ].filter(([,v]) => v).map(([label, value]) => (
                  <div key={label} style={{ textAlign:'center' }}>
                    <div style={{ fontSize:10, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', marginBottom:4 }}>{label}</div>
                    <div style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:6, padding:'6px', fontWeight:700, fontSize:16 }}>{value}"</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'#64748b', marginBottom:8 }}>FEATURES</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {m.doubleSewing && <span className="badge badge-info">Double Sewing</span>}
                {m.zip && <span className="badge badge-info">Zip</span>}
                {m.stud && <span className="badge badge-info">Stud</span>}
                {m.frontPocket && <span className="badge badge-info">Front Pocket</span>}
                {m.net && <span className="badge badge-info">Net</span>}
                {m.surroundType && <span className="badge badge-gray">Surround: {m.surroundType}</span>}
                {m.shalwarType && <span className="badge badge-gray">Shalwar: {m.shalwarType}</span>}
                {m.buttons && <span className="badge badge-gray">Buttons: {m.buttons}</span>}
                {m.sidePocket && <span className="badge badge-gray">Pocket: {m.sidePocket}</span>}
              </div>
            </div>

            {m.extraInformation && (
              <div style={{ background:'#fef3c7', borderRadius:6, padding:10, marginBottom:16, fontSize:13 }}>
                📝 {m.extraInformation}
              </div>
            )}

            {/* Pricing */}
            <div style={{ background:'#f0fdf4', borderRadius:8, padding:14, display:'flex', gap:24 }}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>PRICE</div>
                <div style={{ fontSize:20, fontWeight:700, color:'#1e293b' }}>SAR {m.price || 0}</div>
              </div>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>ADVANCE</div>
                <div style={{ fontSize:20, fontWeight:700, color:'#10b981' }}>SAR {m.advance || 0}</div>
              </div>
              {m.discount > 0 && (
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>DISCOUNT</div>
                  <div style={{ fontSize:20, fontWeight:700, color:'#d97706' }}>- SAR {m.discount}</div>
                </div>
              )}
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>REMAINING</div>
                <div style={{ fontSize:20, fontWeight:700, color: m.remaining > 0 ? '#ef4444' : '#10b981' }}>
                  SAR {m.remaining || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}